/* eslint-disable no-undef */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import fetchData from "../../functions/fetchData";
import { message } from "antd";
import axios from "axios";
import { getHeaders } from "../../utility/getHeader";
import Header from "../../components/Header";
import Layout from "../../layout/Layout";
import { 
    Form, 
    Input, 
    Select, 
    Button, 
    Table, 
    Modal, 
    Card, 
    Row, 
    Col, 
    Statistic, 
    Space, 
    Tag,
    AutoComplete,
    Checkbox,
    Spin,
    InputNumber,
    DatePicker,
    Divider,
    Badge
} from "antd";
import { 
    PrinterOutlined, 
    EditOutlined, 
    UserOutlined, 
    DollarOutlined, 
    CreditCardOutlined,
    BankOutlined,
    FileTextOutlined,
    CalendarOutlined,
    SaveOutlined,
    CloseOutlined,
    FilterOutlined,
    ReloadOutlined,
    FilePdfOutlined,
    FileExcelOutlined,
    SearchOutlined
} from "@ant-design/icons";
import dayjs from 'dayjs';
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import "./vouchers.css";

const { TextArea } = Input;
const { Option } = Select;

export default function Vouchers() {
    const [form] = Form.useForm();
    const [editForm] = Form.useForm();
    
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState("");
    const [outstandingAmount, setOutstandingAmount] = useState(0);
    const [balanceAfterPayment, setBalanceAfterPayment] = useState(0);
    const [data, setData] = useState([]);
    const [companyInfo, setCompanyInfo] = useState({});
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterMode, setFilterMode] = useState("All");
    const [bankAccounts, setBankAccounts] = useState([]);
    
    // Edit modal states
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingVoucher, setEditingVoucher] = useState(null);
    
    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    
    // Filter states
    const [dateRange, setDateRange] = useState([null, null]);
    const [showFilters, setShowFilters] = useState(false);
    
    // Payment modes with bank account requirement
    const paymentModes = [
        { value: "Cash", label: "💵 Cash / เงินสด", requiresBank: false },
        { value: "Bank", label: "🏦 Bank Transfer / โอนเงิน", requiresBank: true },
        { value: "Credit_Card", label: "💳 Credit Card / บัตรเครดิต", requiresBank: true },
        { value: "QR", label: "📱 QR / ยูพีไอ", requiresBank: true },
        { value: "Cheque", label: "📝 Cheque / เช็ค", requiresBank: true },
    ];
    
    // Fetch customers, bank accounts and company info when component loads
    useEffect(() => {
        fetchData("customers", setCustomers, "id", {});
        fetchBankAccounts();
        fetchCompanyInfo();
        fetchReceiptVouchers();
    }, []);

    const fetchBankAccounts = async () => {
        try {
            await fetchData("bank_accounts", setBankAccounts, "id", {});
        } catch (error) {
            // console.error('Error fetching bank accounts:', error);
        }
    };

    const fetchCompanyInfo = async () => {
        try {
            const companyData = await fetchData("company_profile", null, "id", {});
            if (companyData && companyData.length > 0) {
                setCompanyInfo(companyData[0]);
            }
        } catch (error) {
            // console.error("Error fetching company info:", error);
        }
    };

    const fetchReceiptVouchers = async () => {
        try {
            await fetchData("receipt_vouchers", setData, "id", {});
        } catch (error) {
            // console.error("Error fetching receipt vouchers:", error);
        }
    };

    const getOutStandingBalance = async (customerId) => {
        if (!customerId) {
            setOutstandingAmount(0);
            return;
        }

        try {
            const res = await axios.get(`/customer-ledger-balance/${customerId}`, getHeaders());
            if (res.data.success) {
                const balance = res.data.data.balance || 0;
                setOutstandingAmount(Math.abs(balance));
            } else {
                message.error("Failed to fetch customer outstanding balance");
            }
        } catch (error) {
            // console.error("Error fetching outstanding balance:", error);
            setOutstandingAmount(0);
        }
    };

    const handleSubmit = async (values) => {
        const selectedCustomerData = customers.find(c => c.id === parseInt(values.customer_id));
        const customerName = selectedCustomerData ? selectedCustomerData.name : 'Unknown Customer';

        const paymentData = {
            customer_id: values.customer_id,
            customer_name: customerName,
            amount: parseFloat(values.amount),
            discount_amount: parseFloat(values.discount_amount) || 0,
            payment_mode: values.payment_mode,
            bank_account_id: values.bank_account_id || null,
            reference_number: values.reference_number || '',
            remarks: values.remarks || '',
            description: values.remarks || '',
            receipt_date: values.receipt_date ? values.receipt_date.format('YYYY-MM-DD') : new Date().toISOString().split('T')[0],
            tds_amount: values.is_tds_receivable ? parseFloat(values.tds_amount) || 0 : 0,
            is_tds_receivable: values.is_tds_receivable || false
        };

        // console.log('=== RECEIPT VOUCHER PAYLOAD DEBUG ===');
        // console.log('Form Values:', values);
        // console.log('Receipt Date from form:', values.receipt_date);
        // console.log('Is receipt_date defined?', values.receipt_date !== undefined);
        // console.log('Is receipt_date a moment object?', moment.isMoment(values.receipt_date));
        // console.log('Receipt Date type:', typeof values.receipt_date);
        // console.log('Receipt Date value:', values.receipt_date);
        // console.log('Receipt Date formatted:', values.receipt_date ? moment(values.receipt_date).format('YYYY-MM-DD') : 'Using current date');
        // console.log('Complete Payload:', paymentData);
        // console.log('=====================================');

        setLoading(true);
        try {
            const res = await axios.post("/receipt-vouchers", paymentData, getHeaders());
            
            if (res.data.success) {
                message.success("Receipt voucher created successfully!");
                await fetchReceiptVouchers();
                form.resetFields();
                setSelectedCustomer("");
                setOutstandingAmount(0);
                setBalanceAfterPayment(0);
            } else {
                message.error(res.data.message || "Failed to create receipt voucher");
            }
        } catch (error) {
            // console.error("Error saving receipt:", error);
            message.error(`Error recording receipt: ${error?.response?.data?.message || error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handlePrintReceipt = async (voucherData) => {
        try {
            setLoading(true);
            
            const customer = customers.find(c => c.id === parseInt(voucherData.customer_id));
            const customerName = customer ? customer.name : (voucherData.customer_name || 'N/A');
            const customerAddress = customer ? customer.address || 'N/A' : 'N/A';
            const customerPhone = customer ? customer.phone || 'N/A' : 'N/A';
            
            const receiptDate = voucherData.receipt_date || voucherData.payment_date || voucherData.created_at;
            const paymentDate = receiptDate ? new Date(receiptDate) : new Date();
            const isValidDate = !isNaN(paymentDate.getTime());
            
            const formattedDate = isValidDate ? paymentDate.toLocaleDateString('th-TH', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            }) : 'วันที่ไม่ระบุ';
            
            const formattedTime = isValidDate ? paymentDate.toLocaleTimeString('th-TH', {
                hour: '2-digit',
                minute: '2-digit'
            }) : 'เวลาไม่ระบุ';

            // Use correct amount field and handle NaN
            const amount = parseFloat(voucherData.amount || voucherData.amount_paid || 0) || 0;
            const tdsAmount = parseFloat(voucherData.tds_amount || 0) || 0;
            const hasTDS = voucherData.is_tds_receivable || tdsAmount > 0;
            const totalAmount = hasTDS ? amount + tdsAmount : amount;
            const amountInText = totalAmount > 0 ? numberToThaiText(totalAmount) : 'ศูนย์บาทถ้วน';
            
            // Process logo - handle blob/base64/buffer
            let logoSrc = '';
            if (companyInfo.logo) {
                try {
                    let logoData;
                    
                    // Check if it's a Buffer-like object (from database BLOB)
                    if (companyInfo.logo.type === 'Buffer' && Array.isArray(companyInfo.logo.data)) {
                        // Convert Buffer array to base64
                        const uint8Array = new Uint8Array(companyInfo.logo.data);
                        let binary = '';
                        for (let i = 0; i < uint8Array.byteLength; i++) {
                            binary += String.fromCharCode(uint8Array[i]);
                        }
                        logoData = btoa(binary);
                        logoSrc = `data:${companyInfo.logo_type || 'image/png'};base64,${logoData}`;
                    } 
                    // Check if it's already a base64 string
                    else if (typeof companyInfo.logo === 'string') {
                        logoData = companyInfo.logo;
                        
                        if (logoData.startsWith('data:image')) {
                            // Already base64 with prefix
                            logoSrc = logoData;
                        } else if (logoData.startsWith('blob:')) {
                            // Blob URL
                            logoSrc = logoData;
                        } else {
                            // Assume it's base64 without prefix
                            logoSrc = `data:${companyInfo.logo_type || 'image/png'};base64,${logoData}`;
                        }
                    }
                } catch (error) {
                    // console.error('Error processing logo:', error);
                    logoSrc = ''; // Fallback to no logo
                }
            }
            
            // Get payment mode with fallback
            const paymentMode = voucherData.payment_mode || voucherData.mode || 'Cash';
            
            // Get reference with fallback
            const referenceNumber = voucherData.reference_id || voucherData.voucher_number || voucherData.reference_number;

            // Helper function within scope
            const getPaymentModeLabel = (mode) => {
                const modes = {
                    'Cash': '💵 เงินสด / Cash',
                    'Bank': '🏦 โอนเงินผ่านธนาคาร / Bank Transfer',
                    'Credit_Card': '💳 บัตรเครดิต / Credit Card',
                    'UPI': '📱 UPI / ยูพีไอ',
                    'Cheque': '📝 เช็ค / Cheque',
                    'Online': '💻 ชำระออนไลน์ / Online Payment'
                };
                return modes[mode] || `💰 ${mode} / ${mode}`;
            };

            const printContent = `
                <!DOCTYPE html>
                <html>
                <head>
                    <meta charset="UTF-8">
                    <title>ใบเสร็จรับเงิน / Receipt</title>
                    <style>
                        @page {
                            size: A4;
                            margin: 8mm;
                        }
                        
                        * {
                            margin: 0;
                            padding: 0;
                            box-sizing: border-box;
                        }
                        
                        body {
                            font-family: 'Sarabun', 'TH Sarabun New', Arial, sans-serif;
                            font-size: 8pt;
                            line-height: 1.2;
                            color: #000;
                            background: white;
                        }
                        
                        .receipt-container {
                            max-width: 190mm;
                            height: 135mm;
                            margin: 0 auto;
                            padding: 4mm;
                            border: 1.5px solid #000;
                            background: linear-gradient(135deg, #fdfdfd 0%, #f8f9fa 100%);
                            display: flex;
                            flex-direction: column;
                        }
                        
                        /* Compact Header */
                        .header-section {
                            display: flex;
                            align-items: center;
                            margin-bottom: 6px;
                            padding-bottom: 6px;
                            border-bottom: 1px solid #000;
                            min-height: 25mm;
                            background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
                            border-radius: 3px;
                            padding: 4px 6px;
                        }
                        
                        .logo-section {
                            flex: 0 0 60px;
                            margin-right: 8px;
                        }
                        
                        .logo-section img {
                            max-width: 115px;
                            max-height: 40px;
                            object-fit: contain;
                        }
                        
                        .company-info {
                            flex: 1;
                            font-size: 7pt;
                        }
                        
                        .company-name {
                            font-size: 9pt;
                            font-weight: bold;
                            margin-bottom: 2px;
                            color: #b8860b;
                        }
                        
                        .receipt-title {
                            text-align: center;
                            font-size: 10pt;
                            font-weight: bold;
                            margin: 4px 0;
                            padding: 4px;
                            background: linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%);
                            border: 1px solid #4caf50;
                            border-radius: 3px;
                            color: #2e7d32;
                        }
                        
                        /* Two Column Layout */
                        .content-section {
                            display: flex;
                            gap: 8px;
                            flex: 1;
                            margin-bottom: 6px;
                        }
                        
                        .left-column, .right-column {
                            flex: 1;
                            font-size: 7pt;
                        }
                        
                        .info-group {
                            margin-bottom: 6px;
                            padding: 3px;
                            background: linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%);
                            border: 1px solid #9c27b0;
                            border-radius: 3px;
                        }
                        
                        .info-group h4 {
                            font-size: 8pt;
                            font-weight: bold;
                            margin-bottom: 3px;
                            padding-bottom: 2px;
                            border-bottom: 1px solid #9c27b0;
                            color: #7b1fa2;
                        }
                        
                        .info-row {
                            display: flex;
                            margin-bottom: 2px;
                        }
                        
                        .info-label {
                            font-weight: bold;
                            min-width: 50px;
                            font-size: 6pt;
                        }
                        
                        .info-value {
                            flex: 1;
                            font-size: 7pt;
                        }
                        
                        /* Amount Section */
                        .amount-section {
                            background: linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%);
                            border: 1px solid #4caf50;
                            padding: 4px;
                            margin: 4px 0;
                            text-align: center;
                            border-radius: 3px;
                        }
                        
                        .amount-value {
                            font-size: 11pt;
                            font-weight: bold;
                            margin-bottom: 2px;
                            color: #2e7d32;
                        }
                        
                        .amount-words {
                            font-size: 6pt;
                            font-style: italic;
                        }
                        
                        .tds-section {
                            background: linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%);
                            border: 1px solid #8CD8F0FF;
                            padding: 3px;
                            margin: 3px 0;
                            border-radius: 3px;
                        }
                        
                        /* Compact Table */
                        table {
                            width: 100%;
                            border-collapse: collapse;
                            margin: 4px 0;
                            font-size: 6pt;
                        }
                        
                        table, th, td {
                            border: 1px solid #000;
                        }
                        
                        th {
                            background: linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%);
                            padding: 2px 4px;
                            text-align: center;
                            font-weight: bold;
                        }
                        
                        td {
                            padding: 2px 4px;
                            background: white;
                        }
                        
                        .text-center { text-align: center; }
                        .text-right { text-align: right; }
                        
                        /* Enhanced Signatures */
                        .signature-section {
                            display: flex;
                            justify-content: space-between;
                            margin-top: 8px;
                            gap: 8px;
                            font-size: 7pt;
                        }
                        
                        .signature-box {
                            text-align: center;
                            flex: 1;
                            padding: 8px;
                            border: 1.5px solid #333;
                            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
                            border-radius: 4px;
                            min-height: 35px;
                            display: flex;
                            flex-direction: column;
                            justify-content: space-between;
                        }
                        
                        .signature-label {
                            font-weight: bold;
                            font-size: 6pt;
                            color: #333;
                            margin-bottom: 8px;
                            text-transform: uppercase;
                            letter-spacing: 0.5px;
                        }
                        
                        .signature-line {
                            border-top: 1.5px solid #333;
                            margin-top: 15px;
                            padding-top: 4px;
                            font-weight: bold;
                            font-size: 5pt;
                            color: #666;
                        }
                        
                        .footer-note {
                            font-size: 5pt;
                            text-align: center;
                            color: #666;
                            margin-top: 4px;
                            padding: 2px;
                        }
                        
                        @media print {
                            body { margin: 0; padding: 0; }
                            .receipt-container { 
                                border: 1.5px solid #000;
                                box-shadow: none;
                                page-break-inside: avoid;
                                background: #fdfdfd !important;
                            }
                            .header-section { background: #f5f5f0 !important; }
                            .receipt-title { 
                                background: #f0f8f0 !important;
                                color: #2e7d32 !important;
                                border: 1px solid #333 !important;
                            }
                            .info-group { 
                                background: #f8f5f8 !important;
                                border: 1px solid #333 !important;
                            }
                            .amount-section { 
                                background: #f0f8f0 !important;
                                border: 1px solid #333 !important;
                            }
                            .tds-section {
                                background: #faf8f0 !important;
                                border: 1px solid #333 !important;
                            }
                            th { background: #d8d8d8 !important; }
                            .signature-box { 
                                background: #f0f0f0 !important;
                                border: 1.5px solid #000 !important;
                            }
                        }
                    </style>
                </head>
                <body>
                    <div class="receipt-container">
                        <!-- Compact Header -->
                        <div class="header-section">
                            ${logoSrc ? `
                            <div class="logo-section">
                                <img src="${logoSrc}" alt="Logo" />
                            </div>
                            ` : ''}
                            <div class="company-info">
                                <div class="company-name">${companyInfo.name || 'COMPANY NAME'}</div>
                                <div>
                                    ${companyInfo.address || 'Address'} | Tel: ${companyInfo.phone_number || 'Phone'}<br>
                                    Tax ID: ${companyInfo.tax_id || 'XXXXXXXXXXXXX'}
                                    ${companyInfo.email ? ` | ${companyInfo.email}` : ''}
                                </div>
                            </div>
                        </div>
                        
                        <div class="receipt-title">ใบเสร็จรับเงิน / RECEIPT VOUCHER</div>
                        
                        <!-- Two Column Content -->
                        <div class="content-section">
                            <!-- Left Column -->
                            <div class="left-column">
                                <div class="info-group">
                                    <h4>Receipt Info</h4>
                                    <div class="info-row">
                                        <span class="info-label">No:</span>
                                        <span class="info-value">${voucherData.transaction_id || 'N/A'}</span>
                                    </div>
                                    <div class="info-row">
                                        <span class="info-label">Date:</span>
                                        <span class="info-value">${formattedDate}</span>
                                    </div>
                                    <div class="info-row">
                                        <span class="info-label">Time:</span>
                                        <span class="info-value">${formattedTime}</span>
                                    </div>
                                    <div class="info-row">
                                        <span class="info-label">Mode:</span>
                                        <span class="info-value">${getPaymentModeLabel(paymentMode)}</span>
                                    </div>
                                    ${referenceNumber ? `
                                    <div class="info-row">
                                        <span class="info-label">Ref:</span>
                                        <span class="info-value">${referenceNumber}</span>
                                    </div>
                                    ` : ''}
                                </div>
                                
                                <div class="info-group">
                                    <h4>Customer Details</h4>
                                    <div class="info-row">
                                        <span class="info-label">Name:</span>
                                        <span class="info-value">${customerName}</span>
                                    </div>
                                    ${customerAddress !== 'N/A' ? `
                                    <div class="info-row">
                                        <span class="info-label">Addr:</span>
                                        <span class="info-value">${customerAddress}</span>
                                    </div>
                                    ` : ''}
                                    ${customerPhone !== 'N/A' ? `
                                    <div class="info-row">
                                        <span class="info-label">Tel:</span>
                                        <span class="info-value">${customerPhone}</span>
                                    </div>
                                    ` : ''}
                                </div>
                            </div>
                            
                            <!-- Right Column -->
                            <div class="right-column">
                                <div class="amount-section">
                                    <div class="amount-value">฿ ${totalAmount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</div>
                                    <div class="amount-words">(${amountInText})</div>
                                </div>
                                
                                ${hasTDS ? `
                                <div class="tds-section">
                                    <h4 style="font-size: 6pt; color: #f57c00; margin-bottom: 2px;">TDS Details</h4>
                                    <div style="font-size: 5pt;">
                                        Received: ฿${amount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}<br>
                                        TDS Deducted: ฿${tdsAmount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}<br>
                                        Total: ฿${totalAmount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                                    </div>
                                </div>
                                ` : ''}
                                
                                ${voucherData.remarks ? `
                                <div class="info-group">
                                    <h4>Remarks</h4>
                                    <div style="font-size: 6pt; line-height: 1.3;">
                                        ${(voucherData.remarks || '').substring(0, 100)}${(voucherData.remarks || '').length > 100 ? '...' : ''}
                                    </div>
                                </div>
                                ` : ''}
                                
                                <div class="info-group">
                                    <h4>Payment Details</h4>
                                    <table>
                                        <thead>
                                            <tr>
                                                <th style="width: 50%;">Mode</th>
                                                <th style="width: 50%;">Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td class="text-center">${getPaymentModeLabel(paymentMode).split(' / ')[0]}</td>
                                                <td class="text-center">฿ ${amount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</td>
                                            </tr>
                                            ${hasTDS ? `
                                            <tr>
                                                <td class="text-center">TDS Deducted</td>
                                                <td class="text-center">฿ ${tdsAmount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</td>
                                            </tr>
                                            <tr style="font-weight: bold; background: #f0f8f0;">
                                                <td class="text-center">Total</td>
                                                <td class="text-center">฿ ${totalAmount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</td>
                                            </tr>
                                            ` : ''}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Enhanced Signatures Section -->
                        <div class="signature-section">
                            <div class="signature-box">
                                <div class="signature-label">Received By</div>
                                <div class="signature-line">ผู้รับเงิน</div>
                            </div>
                            <div class="signature-box">
                                <div class="signature-label">Paid By</div>
                                <div class="signature-line">ผู้จ่ายเงิน</div>
                            </div>
                        </div>
                        
                        <div class="footer-note">
                            Generated on ${new Date().toLocaleString('th-TH')} | System: ${companyInfo.name || 'POS System'}
                        </div>
                    </div>
                </body>
                </html>
            `;
                     

            // Open print window with better handling
            const printWindow = window.open("", "_blank", "width=800,height=600,scrollbars=yes");
            
            if (!printWindow) {
                message.error("Pop-up blocked! Please allow pop-ups for this site.");
                setLoading(false);
                return;
            }
            
            printWindow.document.write(printContent);
            printWindow.document.close();

            printWindow.onload = () => {
                printWindow.focus();
                setTimeout(() => {
                    printWindow.print();
                    printWindow.onafterprint = () => {
                        printWindow.close();
                    };
                    setTimeout(() => {
                        if (!printWindow.closed) {
                            printWindow.close();
                        }
                    }, 1000);
                }, 500);
            };

            printWindow.onerror = (error) => {
                // console.error("Print window error:", error);
                message.error("Error opening print window");
            };

            setLoading(false);
            message.success("Receipt generated successfully!");
        } catch (error) {
            // console.error("Error printing receipt:", error);
            message.error("Error generating receipt");
            setLoading(false);
        }
    };

    // Helper function to convert number to Thai text
    const numberToThaiText = (number) => {
        const digits = ['ศูนย์', 'หนึ่ง', 'สอง', 'สาม', 'สี่', 'ห้า', 'หก', 'เจ็ด', 'แปด', 'เก้า'];
        const positions = ['', 'สิบ', 'ร้อย', 'พัน', 'หมื่น', 'แสน', 'ล้าน'];
        
        if (number === 0) return 'ศูนย์บาทถ้วน';
        
        const [intPart, decPart] = number.toFixed(2).split('.');
        let result = '';
        
        // Convert integer part
        const intStr = intPart.toString();
        const len = intStr.length;
        
        for (let i = 0; i < len; i++) {
            const digit = parseInt(intStr[i]);
            const position = len - i - 1;
            
            if (digit === 0) continue;
            
            if (position === 1 && digit === 1) {
                result += 'สิบ';
            } else if (position === 1 && digit === 2) {
                result += 'ยี่สิบ';
            } else if (position === 0 && digit === 1 && len > 1) {
                result += 'เอ็ด';
            } else {
                result += digits[digit] + positions[position];
            }
        }
        
        result += 'บาท';
        
        // Convert decimal part
        if (parseInt(decPart) === 0) {
            result += 'ถ้วน';
        } else {
            const satangStr = decPart.toString();
            let satangText = '';
            
            for (let i = 0; i < satangStr.length; i++) {
                const digit = parseInt(satangStr[i]);
                const position = satangStr.length - i - 1;
                
                if (digit === 0 && position === 1) continue;
                
                if (position === 1 && digit === 1) {
                    satangText += 'สิบ';
                } else if (position === 1 && digit === 2) {
                    satangText += 'ยี่สิบ';
                } else if (position === 0 && digit === 1 && satangStr.length > 1) {
                    satangText += 'เอ็ด';
                } else {
                    satangText += digits[digit];
                    if (position === 1) satangText += 'สิบ';
                }
            }
            
            result += satangText + 'สตางค์';
        }
        
        return result;
    };

    const handleEdit = (voucher) => {
        setEditingVoucher(voucher);
        // Get date from database - check multiple possible field names
        const voucherDate = voucher.voucher_date || voucher.date || voucher.created_at;
        
        editForm.setFieldsValue({
            customer_name: customers.find(c => c.id === parseInt(voucher.customer_id))?.name || voucher.customer_name || '',
            amount: voucher.amount || '',
            discount_amount: voucher.discount_amount || '',
            payment_mode: voucher.payment_mode || '',
            bank_account_id: voucher.bank_account_id || '',
            reference_number: voucher.reference_number || voucher.reference_id || '',
            remarks: voucher.remarks || voucher.description || '',
            tds_amount: voucher.tds_amount || '',
            is_tds_receivable: voucher.is_tds_receivable || false,
            voucher_date: voucherDate ? dayjs(voucherDate) : null
        });
        setShowEditModal(true);
    };

    const handleUpdateVoucher = async (values) => {
        const updateData = {
            customer_id: editingVoucher.customer_id,
            customer_name: customers.find(c => c.id === parseInt(editingVoucher.customer_id))?.name || editingVoucher.customer_name,
            amount: parseFloat(values.amount),
            discount_amount: parseFloat(values.discount_amount) || 0,
            payment_mode: values.payment_mode,
            bank_account_id: values.bank_account_id || null,
            reference_number: values.reference_number || '',
            remarks: values.remarks || '',
            description: values.remarks || '',
            tds_amount: values.is_tds_receivable ? parseFloat(values.tds_amount) || 0 : 0,
            is_tds_receivable: values.is_tds_receivable || false,
            receipt_date: values.voucher_date ? values.voucher_date.format('YYYY-MM-DD') : null
        };

        setLoading(true);
        try {
            const res = await axios.put(`/receipt-vouchers/${editingVoucher.id}`, updateData, getHeaders());
            
            if (res.data.success) {
                message.success("Receipt voucher updated successfully!");
                await fetchReceiptVouchers();
                setShowEditModal(false);
                setEditingVoucher(null);
                editForm.resetFields();
            } else {
                message.error(res.data.message || "Failed to update receipt voucher");
            }
        } catch (error) {
            // console.error("Error updating receipt:", error);
            message.error(`Error updating receipt: ${error?.response?.data?.message || error.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Filter data based on search, filter mode, and date range
    const filteredData = data.filter(item => {
        const matchesSearch = 
            item.transaction_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.customer_id?.toString().includes(searchTerm) ||
            (item.amount || item.amount_paid || 0)?.toString().includes(searchTerm);
        
        const matchesFilter = filterMode === "All" || (item.payment_mode || item.mode) === filterMode;
        
        let matchesDateRange = true;
        if (dateRange && dateRange[0] && dateRange[1]) {
            const itemDate = dayjs(item.receipt_date || item.payment_date);
            const startDate = dayjs(dateRange[0]);
            const endDate = dayjs(dateRange[1]);
            matchesDateRange = itemDate.isBetween(startDate, endDate, null, '[]');
        }
        
        return matchesSearch && matchesFilter && matchesDateRange;
    });

    // Calculate total amount
    const totalAmount = filteredData.reduce((sum, item) => sum + (parseFloat(item.amount || item.amount_paid || 0) || 0), 0);

    // Export to PDF
    const exportToPDF = () => {
        const doc = new jsPDF();
        
        // Add header
        doc.setFontSize(18);
        doc.text('Receipt Vouchers Report', 14, 22);
        
        doc.setFontSize(11);
        doc.setTextColor(100);
        doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 30);
        
        let currentY = 35;
        
        // Filter info
        if (dateRange[0] || dateRange[1] || filterMode !== 'All') {
            currentY += 5;
            doc.setFontSize(10);
            doc.text('Filters:', 14, currentY);
            currentY += 5;
            let filterText = [];
            if (dateRange[0] && dateRange[1]) {
                filterText.push(`Period: ${dayjs(dateRange[0]).format('DD/MM/YYYY')} to ${dayjs(dateRange[1]).format('DD/MM/YYYY')}`);
            }
            if (filterMode !== 'All') filterText.push(`Mode: ${filterMode}`);
            doc.text(filterText.join(' | '), 14, currentY);
            currentY += 5;
        }
        
        // Prepare table data
        const tableData = filteredData.map((item) => {
            const customer = customers.find(c => c.id === parseInt(item.customer_id));
            const customerName = customer ? customer.name : (item.customer_name || `Customer #${item.customer_id || 'Unknown'}`);
            const receiptDate = item.receipt_date || item.payment_date;
            const formattedDate = receiptDate ? new Date(receiptDate).toLocaleDateString('en-GB') : 'N/A';
            
            return [
                item.transaction_id || 'N/A',
                customerName,
                formattedDate,
                item.payment_mode || item.mode || 'N/A',
                `${parseFloat(item.amount || item.amount_paid || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
                item.tds_amount && parseFloat(item.tds_amount) > 0 
                    ? `${parseFloat(item.tds_amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
                    : '-',
                item.reference_id || item.voucher_number || '-'
            ];
        });
        
        doc.autoTable({
            startY: currentY + 5,
            head: [['Txn ID', 'Customer', 'Date', 'Mode', 'Amount', 'TDS', 'Ref ID']],
            body: tableData,
            theme: 'striped',
            headStyles: { fillColor: [67, 160, 71] },
            styles: { fontSize: 8 }
        });
        
        // Add summary
        const finalY = doc.lastAutoTable.finalY + 10;
        doc.setFontSize(12);
        doc.text(`Total Records: ${filteredData.length}`, 14, finalY);
        doc.text(`Total Amount: ${totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 14, finalY + 7);
        
        doc.save(`receipt-vouchers-${new Date().toISOString().split('T')[0]}.pdf`);
        message.success('PDF exported successfully!');
    };

    // Export to Excel
    const exportToExcel = () => {
        const excelData = filteredData.map((item) => {
            const customer = customers.find(c => c.id === parseInt(item.customer_id));
            const customerName = customer ? customer.name : (item.customer_name || `Customer #${item.customer_id || 'Unknown'}`);
            const receiptDate = item.receipt_date || item.payment_date;
            
            return {
                'Transaction ID': item.transaction_id || 'N/A',
                'Customer': customerName,
                'Date': receiptDate ? new Date(receiptDate).toLocaleDateString('en-GB') : 'N/A',
                'Payment Mode': item.payment_mode || item.mode || 'N/A',
                'Amount': parseFloat(item.amount || item.amount_paid || 0),
                'TDS Amount': item.tds_amount && parseFloat(item.tds_amount) > 0 ? parseFloat(item.tds_amount) : 0,
                'Discount Amount': item.discount_amount || 0,
                'Reference ID': item.reference_id || item.voucher_number || '-',
                'Remarks': item.remarks || ''
            };
        });
        
        const ws = XLSX.utils.json_to_sheet(excelData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Receipt Vouchers');
        
        // Add summary row
        const summaryData = [
            {},
            { 'Transaction ID': 'SUMMARY' },
            { 'Transaction ID': 'Total Records:', 'Customer': filteredData.length },
            { 'Transaction ID': 'Total Amount:', 'Customer': `฿${totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}` }
        ];
        
        XLSX.utils.sheet_add_json(ws, summaryData, { skipHeader: true, origin: -1 });
        
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `receipt-vouchers-${new Date().toISOString().split('T')[0]}.xlsx`);
        message.success('Excel exported successfully!');
    };

    // Table columns for Ant Design
    const columns = [
        {
            title: 'Txn ID',
            dataIndex: 'transaction_id',
            key: 'transaction_id',
            width: 150,
            fixed: 'left',
            render: (text) => <strong>{text}</strong>
        },
        {
            title: 'Customer',
            dataIndex: 'customer_id',
            key: 'customer',
            width: 200,
            render: (customerId, record) => {
                const customer = customers.find(c => c.id === parseInt(customerId));
                return customer ? customer.name : (record.customer_name || `Customer #${customerId || 'Unknown'}`);
            }
        },
        {
            title: 'Date',
            dataIndex: 'receipt_date',
            key: 'receipt_date',
            width: 180,
            render: (date, record) => {
                const receiptDate = date || record.payment_date;
                return receiptDate ? new Date(receiptDate).toLocaleDateString('en-GB') : 'Invalid Date';
            }
        },
        {
            title: 'Mode',
            dataIndex: 'payment_mode',
            key: 'payment_mode',
            width: 120,
            render: (mode, record) => {
                const paymentMode = mode || record.mode || 'Unknown';
                const colorMap = {
                    'Cash': 'green',
                    'Bank': 'blue',
                    'Cheque': 'gold',
                    'Credit_Card': 'purple',
                    'QR': 'cyan'
                };
                return <Tag color={colorMap[paymentMode] || 'default'}>{paymentMode}</Tag>;
            }
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            width: 120,
            align: 'right',
            render: (amount, record) => {
                const amt = parseFloat(amount || record.amount_paid || 0) || 0;
                return <strong>฿ {amt.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</strong>;
            }
        },
        {
            title: 'TDS Amount',
            dataIndex: 'tds_amount',
            key: 'tds_amount',
            width: 120,
            align: 'right',
            render: (tds) => {
                if (tds && parseFloat(tds) > 0) {
                    return <Tag color="orange">฿ {parseFloat(tds).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</Tag>;
                }
                return '-';
            }
        },
        {
            title: 'Ref ID',
            dataIndex: 'reference_id',
            key: 'reference_id',
            width: 150,
            render: (ref, record) => ref || record.voucher_number || '-'
        },
        {
            title: 'Action',
            key: 'action',
            width: 120,
            fixed: 'right',
            align: 'center',
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        icon={<EditOutlined />}
                        size="small"
                        onClick={() => handleEdit(record)}
                    />
                    <Button
                        type="default"
                        icon={<PrinterOutlined />}
                        size="small"
                        onClick={() => handlePrintReceipt(record)}
                    />
                </Space>
            )
        }
    ];

    const handleFormValuesChange = (changedValues, allValues) => {
        const payment = parseFloat(allValues.amount) || 0;
        const tds = allValues.is_tds_receivable ? (parseFloat(allValues.tds_amount) || 0) : 0;
        const discount = parseFloat(allValues.discount_amount) || 0;
        const totalPayment = payment + tds + discount;
        const remaining = outstandingAmount - totalPayment;
        setBalanceAfterPayment(remaining >= 0 ? remaining : 0);
    };

    return (
        <Layout>
            <Header title="Receipt Vouchers / ใบเสร็จรับเงิน" />
            
            <Spin spinning={loading} size="large">
                <Row gutter={[16, 16]}>
                    {/* Left Panel - Payment Form */}
                    <Col xs={24} lg={8}>
                        <Card 
                            title={<><FileTextOutlined /> Create Receipt Voucher</>}
                            bordered={false}
                            style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
                            headStyle={{ background: 'linear-gradient(135deg, #667eea 0%, #3c93daff 100%)', color: 'white' }}
                        >
                            <Form
                                form={form}
                                layout="vertical"
                                onFinish={handleSubmit}
                                onValuesChange={handleFormValuesChange}
                                initialValues={{
                                    receipt_date: dayjs()
                                }}
                            >
                                {/* Receipt Date */}
                                <Form.Item
                                    label={<><CalendarOutlined /> Receipt Date</>}
                                    name="receipt_date"
                                    rules={[{ required: true, message: 'Please select receipt date' }]}
                                >
                                    <DatePicker
                                        style={{ width: '100%' }}
                                        format="DD/MM/YYYY"
                                        placeholder="Select date"
                                    />
                                </Form.Item>

                                {/* Customer Selection */}
                                <Form.Item
                                    label={<><UserOutlined /> Customer Name</>}
                                    name="customer_id"
                                    rules={[{ required: true, message: 'Please select a customer' }]}
                                >
                                    <Select
                                        showSearch
                                        placeholder="Search and select customer"
                                        optionFilterProp="children"
                                        onChange={(value) => {
                                            setSelectedCustomer(value);
                                            getOutStandingBalance(value);
                                        }}
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                        options={customers.map(customer => ({
                                            value: customer.id,
                                            label: customer.name
                                        }))}
                                    />
                                </Form.Item>

                                {/* Outstanding Amount Display */}
                                {selectedCustomer && (
                                    <Card size="small" style={{ marginBottom: 16, background: '#fff3cd' }}>
                                        <Statistic
                                            title="Outstanding Amount"
                                            value={outstandingAmount}
                                            prefix="฿"
                                            precision={2}
                                            valueStyle={{ color: '#ff6b6b' }}
                                        />
                                    </Card>
                                )}

                                {/* Payment Amount */}
                                <Form.Item
                                    label={<><DollarOutlined /> Payment Amount</>}
                                    name="amount"
                                    rules={[{ required: true, message: 'Please enter payment amount' }]}
                                >
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        min={0}
                                        precision={2}
                                        placeholder="Enter amount"
                                        prefix="฿"
                                    />
                                </Form.Item>

                                {/* Discount Amount */}
                                <Form.Item
                                    label="Discount Amount"
                                    name="discount_amount"
                                >
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        min={0}
                                        precision={2}
                                        placeholder="Enter discount"
                                        prefix="฿"
                                    />
                                </Form.Item>

                                {/* Payment Mode */}
                                <Form.Item
                                    label={<><CreditCardOutlined /> Payment Mode</>}
                                    name="payment_mode"
                                    rules={[{ required: true, message: 'Please select payment mode' }]}
                                >
                                    <Select placeholder="Select payment mode">
                                        {paymentModes.map(mode => (
                                            <Option key={mode.value} value={mode.value}>
                                                {mode.label}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>

                                {/* Bank Account - Conditional */}
                                <Form.Item
                                    noStyle
                                    shouldUpdate={(prevValues, currentValues) => 
                                        prevValues.payment_mode !== currentValues.payment_mode
                                    }
                                >
                                    {({ getFieldValue }) => {
                                        const selectedMode = getFieldValue('payment_mode');
                                        const requiresBank = paymentModes.find(m => m.value === selectedMode)?.requiresBank;
                                        
                                        return requiresBank ? (
                                            <Form.Item
                                                label={<><BankOutlined /> Bank Account</>}
                                                name="bank_account_id"
                                                rules={[{ required: true, message: 'Please select bank account' }]}
                                            >
                                                <Select placeholder="Select bank account">
                                                    {bankAccounts.map(account => (
                                                        <Option key={account.id} value={account.id}>
                                                            {account.bank_name} - {account.account_number}
                                                        </Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                        ) : null;
                                    }}
                                </Form.Item>

                                {/* TDS Checkbox */}
                                <Form.Item
                                    name="is_tds_receivable"
                                    valuePropName="checked"
                                >
                                    <Checkbox>TDS Deducted (Receivable)</Checkbox>
                                </Form.Item>

                                {/* TDS Amount - Conditional */}
                                <Form.Item
                                    noStyle
                                    shouldUpdate={(prevValues, currentValues) => 
                                        prevValues.is_tds_receivable !== currentValues.is_tds_receivable
                                    }
                                >
                                    {({ getFieldValue }) =>
                                        getFieldValue('is_tds_receivable') ? (
                                            <Form.Item
                                                label="TDS Amount"
                                                name="tds_amount"
                                            >
                                                <InputNumber
                                                    style={{ width: '100%' }}
                                                    min={0}
                                                    precision={2}
                                                    placeholder="Enter TDS amount"
                                                    prefix="฿"
                                                />
                                            </Form.Item>
                                        ) : null
                                    }
                                </Form.Item>

                                {/* Reference Number */}
                                <Form.Item
                                    label="Reference Number"
                                    name="reference_number"
                                >
                                    <Input placeholder="Enter reference number" />
                                </Form.Item>

                                {/* Remarks */}
                                <Form.Item
                                    label="Remarks"
                                    name="remarks"
                                >
                                    <TextArea rows={3} placeholder="Enter any additional notes" />
                                </Form.Item>

                                {/* Balance After Payment */}
                                {selectedCustomer && (
                                    <Card 
                                        size="small" 
                                        style={{ 
                                            marginBottom: 16, 
                                            background: balanceAfterPayment === 0 ? '#d4edda' : '#fff3cd'
                                        }}
                                    >
                                        <Statistic
                                            title="Balance After Payment"
                                            value={balanceAfterPayment}
                                            prefix="฿"
                                            precision={2}
                                            valueStyle={{ 
                                                color: balanceAfterPayment === 0 ? '#28a745' : '#ff6b6b',
                                                fontWeight: 'bold'
                                            }}
                                        />
                                    </Card>
                                )}

                                {/* Submit Button */}
                                <Form.Item>
                                    <Button 
                                        type="primary" 
                                        htmlType="submit" 
                                        block
                                        size="large"
                                        icon={<SaveOutlined />}
                                        loading={loading}
                                    >
                                        Save Payment
                                    </Button>
                                </Form.Item>
                            </Form>
                        </Card>
                    </Col>

                    {/* Right Panel - Payment History */}
                    <Col xs={24} lg={16}>
                        <Card 
                            title={<><FileTextOutlined /> Payment History</>}
                            bordered={false}
                            style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
                            headStyle={{ background: 'linear-gradient(135deg, #667eea 0%, #3c93daff 100%)', color: 'white' }}
                        >
                            {/* Search and Filter Section */}
                            <Row gutter={16} style={{ marginBottom: 16 }}>
                                <Col xs={24} md={6}>
                                    <Input
                                        placeholder="🔍 Search..."
                                        prefix={<SearchOutlined />}
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        allowClear
                                    />
                                </Col>
                                <Col xs={24} md={8}>
                                    <Select
                                        style={{ width: '100%' }}
                                        placeholder="Filter by payment mode"
                                        value={filterMode}
                                        onChange={setFilterMode}
                                    >
                                        <Option value="All">📋 All Modes</Option>
                                        <Option value="Cash">💵 Cash</Option>
                                        <Option value="Bank">🏦 Bank</Option>
                                        <Option value="Cheque">📝 Cheque</Option>
                                        <Option value="QR">📱 QR</Option>
                                        <Option value="Credit_Card">💳 Credit Card</Option>
                                    </Select>
                                </Col>
                                <Col xs={24} md={10}>
                                    <Space>
                                        <Button
                                            icon={<FilterOutlined />}
                                            onClick={() => setShowFilters(!showFilters)}
                                            type={showFilters ? 'primary' : 'default'}
                                        >
                                            Filters
                                        </Button>
                                        <Button
                                            icon={<FilePdfOutlined />}
                                            onClick={exportToPDF}
                                            danger
                                        >
                                            PDF
                                        </Button>
                                        <Button
                                            icon={<FileExcelOutlined />}
                                            onClick={exportToExcel}
                                            style={{ background: '#107c41', color: 'white', borderColor: '#107c41' }}
                                        >
                                            Excel
                                        </Button>
                                        <Button
                                            icon={<ReloadOutlined />}
                                            onClick={() => {
                                                setSearchTerm('');
                                                setFilterMode('All');
                                                setDateRange([null, null]);
                                            }}
                                        >
                                            Reset
                                        </Button>
                                    </Space>
                                </Col>
                            </Row>

                            {showFilters && (
                                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                                    <Col xs={24} md={12}>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: 8 }}>
                                                <CalendarOutlined /> Date Range
                                            </label>
                                            <DatePicker.RangePicker
                                                style={{ width: '100%' }}
                                                value={dateRange}
                                                onChange={(dates) => setDateRange(dates)}
                                                format="DD/MM/YYYY"
                                            />
                                        </div>
                                    </Col>
                                    <Col xs={24} md={12}>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: 8 }}>
                                                Records Per Page
                                            </label>
                                            <Select
                                                style={{ width: '100%' }}
                                                value={pageSize}
                                                onChange={(value) => setPageSize(value)}
                                            >
                                                <Option value={5}>5 records</Option>
                                                <Option value={10}>10 records</Option>
                                                <Option value={25}>25 records</Option>
                                                <Option value={50}>50 records</Option>
                                                <Option value={100}>100 records</Option>
                                            </Select>
                                        </div>
                                    </Col>
                                </Row>
                            )}

                            {/* Table */}
                            <Table
                                columns={columns}
                                dataSource={filteredData}
                                rowKey="id"
                                scroll={{ x: 1200 }}
                                pagination={{
                                    current: currentPage,
                                    pageSize: pageSize,
                                    total: filteredData.length,
                                    showSizeChanger: true,
                                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
                                    pageSizeOptions: ['5', '10', '25', '50', '100'],
                                    onChange: (page, size) => {
                                        setCurrentPage(page);
                                        setPageSize(size);
                                    }
                                }}
                            />

                            <Divider />

                            {/* Summary Cards */}
                            <Row gutter={16}>
                                <Col xs={24} sm={8}>
                                    <Card size="small">
                                        <Statistic
                                            title="Total Records"
                                            value={filteredData.length}
                                            valueStyle={{ color: '#3f8600' }}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={24} sm={8}>
                                    <Card size="small">
                                        <Statistic
                                            title="Total Amount"
                                            value={totalAmount}
                                            prefix="฿"
                                            precision={2}
                                            valueStyle={{ color: '#cf1322' }}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={24} sm={8}>
                                    <Card size="small">
                                        <Statistic
                                            title="Latest Payment"
                                            value={
                                                filteredData.length > 0 && (filteredData[0].receipt_date || filteredData[0].payment_date)
                                                    ? new Date(filteredData[0].receipt_date || filteredData[0].payment_date).toLocaleDateString('th-TH')
                                                    : '-'
                                            }
                                            valueStyle={{ color: '#1890ff', fontSize: '16px' }}
                                        />
                                    </Card>
                                </Col>
                            </Row>
                        </Card>
                    </Col>
                </Row>
            </Spin>

            {/* Edit Modal */}
            <Modal
                title={<><EditOutlined /> Edit Receipt Voucher - {editingVoucher?.transaction_id}</>}
                open={showEditModal}
                onCancel={() => {
                    setShowEditModal(false);
                    setEditingVoucher(null);
                    editForm.resetFields();
                }}
                width={800}
                footer={null}
            >
                <Form
                    form={editForm}
                    layout="vertical"
                    onFinish={handleUpdateVoucher}
                >
                    <Row gutter={16}>
                        {/* Customer Name (Read-only) */}
                        <Col xs={24} md={12}>
                            <Form.Item
                                label={<><UserOutlined /> Customer Name</>}
                                name="customer_name"
                            >
                                <Input disabled />
                            </Form.Item>
                        </Col>

                        {/* Voucher Date */}
                        <Col xs={24} md={12}>
                            <Form.Item
                                label={<><CalendarOutlined /> Voucher Date</>}
                                name="voucher_date"
                                rules={[{ required: true, message: 'Please select voucher date' }]}
                            >
                                <DatePicker 
                                    style={{ width: '100%' }}
                                    format="YYYY-MM-DD"
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label={<><DollarOutlined /> Payment Amount</>}
                                name="amount"
                                rules={[{ required: true, message: 'Please enter payment amount' }]}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    min={0}
                                    precision={2}
                                    placeholder="Enter amount"
                                    prefix="฿"
                                />
                            </Form.Item>
                        </Col>

                        {/* Discount Amount */}
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Discount Amount"
                                name="discount_amount"
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    min={0}
                                    precision={2}
                                    placeholder="Enter discount"
                                    prefix="฿"
                                />
                            </Form.Item>
                        </Col>

                        {/* Payment Mode */}
                        <Col xs={24} md={12}>
                            <Form.Item
                                label={<><CreditCardOutlined /> Payment Mode</>}
                                name="payment_mode"
                                rules={[{ required: true, message: 'Please select payment mode' }]}
                            >
                                <Select placeholder="Select payment mode">
                                    {paymentModes.map(mode => (
                                        <Option key={mode.value} value={mode.value}>
                                            {mode.label}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        {/* Bank Account - Conditional */}
                        <Col xs={24} md={12}>
                            <Form.Item
                                noStyle
                                shouldUpdate={(prevValues, currentValues) => 
                                    prevValues.payment_mode !== currentValues.payment_mode
                                }
                            >
                                {({ getFieldValue }) => {
                                    const selectedMode = getFieldValue('payment_mode');
                                    const requiresBank = paymentModes.find(m => m.value === selectedMode)?.requiresBank;
                                    
                                    return requiresBank ? (
                                        <Form.Item
                                            label={<><BankOutlined /> Bank Account</>}
                                            name="bank_account_id"
                                            rules={[{ required: true, message: 'Please select bank account' }]}
                                        >
                                            <Select placeholder="Select bank account">
                                                {bankAccounts.map(account => (
                                                    <Option key={account.id} value={account.id}>
                                                        {account.bank_name} - {account.account_number}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    ) : null;
                                }}
                            </Form.Item>
                        </Col>

                        {/* TDS Checkbox */}
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="is_tds_receivable"
                                valuePropName="checked"
                            >
                                <Checkbox>TDS Deducted (Receivable)</Checkbox>
                            </Form.Item>
                        </Col>

                        {/* TDS Amount - Conditional */}
                        <Col xs={24} md={12}>
                            <Form.Item
                                noStyle
                                shouldUpdate={(prevValues, currentValues) => 
                                    prevValues.is_tds_receivable !== currentValues.is_tds_receivable
                                }
                            >
                                {({ getFieldValue }) =>
                                    getFieldValue('is_tds_receivable') ? (
                                        <Form.Item
                                            label="TDS Amount"
                                            name="tds_amount"
                                        >
                                            <InputNumber
                                                style={{ width: '100%' }}
                                                min={0}
                                                precision={2}
                                                placeholder="Enter TDS amount"
                                                prefix="฿"
                                            />
                                        </Form.Item>
                                    ) : null
                                }
                            </Form.Item>
                        </Col>

                        {/* Reference Number */}
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Reference Number"
                                name="reference_number"
                            >
                                <Input placeholder="Enter reference number" />
                            </Form.Item>
                        </Col>

                        {/* Remarks */}
                        <Col xs={24}>
                            <Form.Item
                                label="Remarks"
                                name="remarks"
                            >
                                <TextArea rows={3} placeholder="Enter any additional notes" />
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Modal Footer Buttons */}
                    <Form.Item>
                        <Space style={{ float: 'right' }}>
                            <Button 
                                onClick={() => {
                                    setShowEditModal(false);
                                    setEditingVoucher(null);
                                    editForm.resetFields();
                                }}
                                icon={<CloseOutlined />}
                            >
                                Cancel
                            </Button>
                            <Button 
                                type="primary" 
                                htmlType="submit"
                                icon={<SaveOutlined />}
                                loading={loading}
                            >
                                Update Receipt
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </Layout>
    );
}
